'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import Icon from '@/components/ui/AppIcon';

const AboutHero = ({ children }: { children: React.ReactNode }) => {
    const containerRef = useRef<HTMLElement>(null);
    const badgeRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const descRef = useRef<HTMLParagraphElement>(null);

    useGSAP(() => {
        const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });

        // Elements are hidden by default via CSS classes to prevent FOUC

        tl.to(badgeRef.current, {
            y: 0,
            autoAlpha: 1, // handles opacity + visibility
            duration: 1.2,
            delay: 0.2
        })
            .to(titleRef.current, {
                y: 0,
                autoAlpha: 1,
                duration: 1.4,
                stagger: 0.1 // if we split text later, but good for robust future
            }, '-=1.0')
            .to(descRef.current, {
                y: 0,
                autoAlpha: 1,
                duration: 1.2
            }, '-=1.0');

    }, { scope: containerRef });

    return (
        <section ref={containerRef} className="relative py-0 overflow-hidden min-h-screen flex items-center justify-center bg-slate-50">
            {/* 3D Background - Consistent with Services/Home */}
            <div className="absolute inset-0 overflow-hidden opacity-100 z-0">
                {children}
            </div>

            {/* Overlay Gradient (Subtle Light Theme) */}
            <div className="absolute inset-0 bg-gradient-to-b from-slate-50/10 via-slate-50/40 to-slate-50/90 z-0 pointer-events-none" />

            {/* Text Content - Centered */}
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 flex flex-col items-center justify-center h-full text-center pt-20">
                {/* Badge */}
                <div ref={badgeRef} className="inline-flex items-center space-x-2 px-4 py-2 bg-white/90 rounded-full border border-indigo-100 shadow-sm mb-8 opacity-0 translate-y-8 invisible">
                    <span className="flex h-2 w-2 relative">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                    </span>
                    <span className="text-sm font-bold text-indigo-900 uppercase tracking-widest">Our DNA</span>
                </div>

                {/* Headline */}
                <h1 ref={titleRef} className="text-5xl sm:text-7xl lg:text-9xl font-black text-slate-900/90 mb-8 tracking-tight leading-[1.1] opacity-0 translate-y-8 invisible">
                    Where Technical <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-violet-600 to-blue-600">
                        Excellence Meets
                    </span> <br />
                    Strategic Growth
                </h1>

                {/* Subheadline */}
                <p ref={descRef} className="max-w-3xl text-lg sm:text-2xl text-slate-600 font-medium leading-relaxed mb-10 opacity-0 translate-y-8 invisible">
                    Founded by specialists who believe brands grow best when engineering and visibility are designed together.
                </p>


            </div>
        </section>
    );
};

export default AboutHero;
